import { interpolateClosedSpline, interpolateClosedHeights, type Point2D } from "./spline";

export interface Track {
  // Starting position
  startX: number;
  startY: number;
  startAngle: number;

  // Centerline points for rendering and collision (world coords, closed loop)
  centerline: { x: number; y: number }[];

  // Height at each centerline point (meters above ground plane, parallel to centerline[])
  heights: number[];

  // Track half-width
  halfWidth: number;

  // Start/finish line position (index into centerline)
  startFinishIndex: number;
}

/**
 * Creates the Grand Prix circuit — a ~1.8km track with 8 corners.
 *
 * Layout (top-down, +x right, +y up, driven clockwise):
 *
 *                       T1 (fast right)
 *   Start/Finish ----→ ╮
 *                       │
 *                       ╰─── T2 (tight right)
 *                             │
 *        T4 (fast left) ─── T3 (hairpin left)
 *        │
 *        ╰── T5 (right kink)
 *             │
 *   T7 ──── T6 (chicane)
 *   │
 *   ╰──── back to Start/Finish
 *
 * The car starts on the main straight heading +x (angle = 0).
 */
export function createGPTrack(): Track {
  const halfWidth = 10;

  // Control points defining the circuit shape (closed loop, clockwise)
  // Each point is a waypoint the spline passes through
  const controlPoints: Point2D[] = [
    // Main straight (heading +x)
    { x: -60, y: 80 },
    { x: 80, y: 80 },
    // T1: fast right sweeper
    { x: 180, y: 80 },
    { x: 230, y: 50 },
    // T2: medium right
    { x: 240, y: -10 },
    { x: 210, y: -50 },
    // T3: left hairpin
    { x: 160, y: -80 },
    { x: 100, y: -70 },
    // Short straight + T4: fast left
    { x: 50, y: -40 },
    { x: 10, y: 0 },
    // T5: right kink
    { x: -20, y: 30 },
    // T6-T7: sweeping left back to straight
    { x: -60, y: 50 },
    { x: -80, y: 70 },
  ];

  const centerline = interpolateClosedSpline(controlPoints, 30);

  // Height at each control point (meters above the start/finish plane).
  // 0=main straight · 1=main straight · 2=T1 entry (rising) · 3=T1 peak ·
  // 4=T2 (flattens) · 5=T2 exit (descending) · 6=T3 hairpin dip ·
  // 7=T3 exit · 8=T4 entry (recovering) · 9=T4 ·
  // 10=T5 (hill) · 11=back straight peak · 12=descent to main straight
  const heightControlPoints = [0, 0, 3, 4, 2, -2, -6, -4, 0, 3, 7, 9, 5];
  const heights = interpolateClosedHeights(heightControlPoints, 30);

  // Start position: middle of main straight, on the centerline
  // The straight runs from control point 0 to 1, so index ~15 (halfway through segment 0)
  const startFinishIndex = 15;
  const startPt = centerline[startFinishIndex];

  // Start angle: direction along the centerline at start point
  const nextPt = centerline[startFinishIndex + 1];
  const startAngle = Math.atan2(
    nextPt.y - startPt.y,
    nextPt.x - startPt.x
  );

  return {
    startX: startPt.x,
    startY: startPt.y,
    startAngle,
    centerline,
    heights,
    halfWidth,
    startFinishIndex,
  };
}

/**
 * Creates the Monza circuit (Autodromo Nazionale Monza) — ~5.8km, clockwise.
 *
 * Key features: long straights, tight chicanes, fast sweepers.
 * Sections: Main straight → Variante del Rettifilo → Curva Grande →
 *           Variante della Roggia → Lesmo 1 & 2 → Ascari chicane →
 *           Back straight → Parabolica → back to main straight.
 */
export function createMonzaTrack(): Track {
  const halfWidth = 10;

  // Monza - Temple of Speed (~5.8km, clockwise)
  // The track is famous for its long straights and tight chicanes
  const controlPoints: Point2D[] = [
    // Main straight (heading +x)
    { x: -100, y: 200 },
    { x: 100, y: 200 },
    { x: 250, y: 200 },
    // Variante del Rettifilo (chicane: right-left)
    { x: 350, y: 185 },
    { x: 380, y: 160 },
    // Curva Grande (fast right sweeper)
    { x: 420, y: 100 },
    { x: 400, y: 30 },
    // Variante della Roggia (chicane: right-left)
    { x: 360, y: -10 },
    { x: 340, y: -40 },
    // Lesmo 1
    { x: 300, y: -80 },
    { x: 260, y: -100 },
    // Lesmo 2
    { x: 220, y: -130 },
    { x: 180, y: -140 },
    // Short straight
    { x: 120, y: -130 },
    // Ascari chicane (right-left-right)
    { x: 70, y: -100 },
    { x: 40, y: -60 },
    { x: 20, y: -20 },
    // Back straight
    { x: -40, y: 10 },
    { x: -120, y: 50 },
    // Parabolica (long constant-radius right)
    { x: -200, y: 80 },
    { x: -250, y: 120 },
    { x: -230, y: 170 },
    // Back to main straight
    { x: -180, y: 200 },
  ];

  const centerline = interpolateClosedSpline(controlPoints, 30);

  // Start position on the main straight
  const startFinishIndex = 15;
  const startPt = centerline[startFinishIndex];
  const nextPt = centerline[startFinishIndex + 1];
  const startAngle = Math.atan2(nextPt.y - startPt.y, nextPt.x - startPt.x);

  return {
    startX: startPt.x,
    startY: startPt.y,
    startAngle,
    centerline,
    heights: new Array(centerline.length).fill(0),
    halfWidth,
    startFinishIndex,
  };
}

/**
 * Creates the Spa-Francorchamps circuit — ~7km, clockwise.
 *
 * Key sections: Start/finish straight → La Source hairpin → Eau Rouge/Raidillon →
 * Kemmel straight → Les Combes chicane → Malmedy → Rivage hairpin →
 * Pouhon double-left sweeper → Fagnes chicane → Stavelot → Blanchimont →
 * Bus Stop chicane → back to start/finish.
 */
export function createSpaTrack(): Track {
  const halfWidth = 10;

  // Spa-Francorchamps — 7km circuit, clockwise
  const controlPoints: Point2D[] = [
    // Start/finish straight
    { x: -50, y: 300 },
    { x: 80, y: 300 },
    // La Source hairpin (tight right)
    { x: 150, y: 280 },
    { x: 140, y: 240 },
    // Down to Eau Rouge (descending left then right)
    { x: 100, y: 180 },
    { x: 80, y: 120 },
    // Raidillon (steep uphill right)
    { x: 100, y: 60 },
    { x: 150, y: 20 },
    // Kemmel straight (long, fast)
    { x: 280, y: -10 },
    { x: 400, y: -30 },
    // Les Combes (right-left-right chicane)
    { x: 460, y: -50 },
    { x: 480, y: -90 },
    { x: 450, y: -120 },
    // Malmedy
    { x: 400, y: -140 },
    // Rivage hairpin (tight left)
    { x: 360, y: -180 },
    { x: 320, y: -160 },
    // Descent to Pouhon
    { x: 260, y: -200 },
    // Pouhon (double-left sweeper)
    { x: 180, y: -250 },
    { x: 120, y: -280 },
    // Fagnes
    { x: 60, y: -260 },
    { x: 20, y: -230 },
    // Stavelot
    { x: -30, y: -180 },
    { x: -60, y: -120 },
    // Blanchimont (fast left kink)
    { x: -100, y: -40 },
    { x: -120, y: 40 },
    // Bus Stop chicane
    { x: -130, y: 140 },
    { x: -110, y: 200 },
    // Back to start/finish
    { x: -100, y: 260 },
  ];

  const centerline = interpolateClosedSpline(controlPoints, 30);

  const startFinishIndex = 15;
  const startPt = centerline[startFinishIndex];
  const nextPt = centerline[startFinishIndex + 1];
  const startAngle = Math.atan2(nextPt.y - startPt.y, nextPt.x - startPt.x);

  return {
    startX: startPt.x,
    startY: startPt.y,
    startAngle,
    centerline,
    heights: new Array(centerline.length).fill(0),
    halfWidth,
    startFinishIndex,
  };
}

/**
 * Creates a simple oval track for initial physics testing.
 */
export function createOvalTrack(): Track {
  const straightLen = 200;
  const curveRadius = 60;
  const halfWidth = 12;
  const halfStraight = straightLen / 2;

  const centerline: { x: number; y: number }[] = [];
  const steps = 200;

  for (let i = 0; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    centerline.push({
      x: -halfStraight + t * straightLen,
      y: curveRadius,
    });
  }

  for (let i = 1; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    const a = Math.PI / 2 - t * Math.PI;
    centerline.push({
      x: halfStraight + Math.cos(a) * curveRadius,
      y: Math.sin(a) * curveRadius,
    });
  }

  for (let i = 1; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    centerline.push({
      x: halfStraight - t * straightLen,
      y: -curveRadius,
    });
  }

  for (let i = 1; i <= steps / 4; i++) {
    const t = i / (steps / 4);
    const a = -Math.PI / 2 - t * Math.PI;
    centerline.push({
      x: -halfStraight + Math.cos(a) * curveRadius,
      y: Math.sin(a) * curveRadius,
    });
  }

  return {
    startX: 0,
    startY: curveRadius,
    startAngle: 0,
    centerline,
    heights: new Array(centerline.length).fill(0),
    halfWidth,
    startFinishIndex: 0,
  };
}
